import React from "react"
import redArrow from "../../../view/assets/arrowRed.png"
import greenArrow from "../../../view/assets/arrowGreen.png"
import pendingIcon from "../../../view/assets/error.svg"
import PropTypes from "prop-types"
import Status from "./Status"
import { useDispatch, useSelector } from "react-redux"
import uuid from "uuid"
import { initiateTransaction } from "../../../store/reducers/transactions/actions"
import { t } from "@lingui/macro"
import { intToArabic } from "../../../view/shared/utilities/arabicNumParser"
import { useLingui } from "@lingui/react"

const itemStyle = { display: "flex", alignItems: "baseline" }
const textStyle = { display: "flex", justifyContent: "space-between" }
const buttonStyle = {
  width: "100%",
  color: "#fff",
  background: "#e36f1e",
  border: "solid 1px #e36f1e",
  borderRadius: "3px",
  marginTop: "14px",
}

const View = (props) => {
  const dispatch = useDispatch()
  const { i18n } = useLingui()

  // This is crucial for handling the dynamic `getCustomType` function.
  // Define translations inside component so they use the current i18n context
  const translations = React.useMemo(() => ({
    // Base transaction types
    overview: t`Members Overview`,
    welfare: t`Welfare Contribution`,
    welfare_request: t`Welfare Request`,
    buy_shares: t`Buy Shares`,
    create_loan: t`Create Loan`,
    repay_loan: t`Repay Loan`,
    write_off_loan: t`Write-off Loan`,
    pay_fine: t`Pay fine`,
    membership_fee: t`Membership Fee`,
    sell_shares: t`Sell Shares`,
    transaction_amount: t`Transaction Amount`,
    other_income: t`Group Income`,
    other_expenses: t`Group Expenses`,
    cashout: t`Cash out`,
    group_income: t`Group Income`,
    group_expense: t`Group Expenses`,
    fine_payment: t`Fine Payment`,
    welfare_contribution: t`Welfare Contribution`,
    loan_repayment: t`Loan Repayment`,
    loan_interest_repayment: t`Loan Interest Repayment`,
    fund_agri: t`Agricultural Fund Contribution`,
    savings_cash_out: t`Savings cashout`,
    group_income_fund: t`Group income - Savings`,
    group_income_fine: t`Group income - Fine`,
    group_income_welfare: t`Group income - Welfare`,
    group_expense_fund: t`Group expense - Savings`,
    group_expense_fine: t`Group expense - Fine`,
    group_expense_welfare: t`Group expense - Welfare`,
    // Reversed transaction types
    fine_payment_reversal: t`Fine Payment Reversed`,
    buy_shares_reversal: t`Buy Shares Reversed`,
    welfare_contribution_reversal: t`Welfare Contribution Reversed`,
    sell_shares_reversal: t`Sell Shares Reversed`,
    loan_interest_repayment_reversal: t`Loan Interest Repayment Reversed`,
    welfare_request_reversal: t`Welfare Request Reversed`,
    membership_fee_reversal: t`Membership Fee Reversed`,
    loan_repayment_reversal: t`Loan Repayment Reversed`,
    // Dynamic string patterns
    reversed_pattern: t`Reversed {0}`,
    general_pattern: t`{0}`,
    add_loan_interest: t`Add Loan Interest`,
    membership_cash_out: t`Membership Cash Out`,
    loan_interest_cash_out: t`Loan Interest Cash Out`,
    savings_cash_out: t`Savings Cash Out`,
    welfare_cash_out: t`Welfare Cash Out`,
    fine_cash_out: t`Fine Cash Out`,
  }), [i18n.locale])
  const getCustomType = (type) => {
    // Normalize the type to use underscores (our translations object uses snake_case keys)
    const normalizedType = type.replace(/ /g, "_")
    const cleanType = type.replace(/_/g, " ")

    // Handle reversed transactions (e.g., "buy_shares_reversed" or "reverse_buy_shares")
    if (normalizedType.endsWith("_reversed")) {
      const originalType = normalizedType.replace("_reversed", "")
      const translationKey = `${originalType}_reversal`;
      const translation = translations[translationKey];
      if (translation) {
        return i18n._(translation);
      }
      return i18n._(translations.reversed_pattern, { 0: cleanType });
    }

    if (normalizedType.startsWith("reverse_")) {
      const originalType = normalizedType.replace("reverse_", "")
      const translationKey = `${originalType}_reversal`;
      const translation = translations[translationKey];
      if (translation) {
        return i18n._(translation);
      }
      return i18n._(translations.reversed_pattern, { 0: cleanType });
    }

    const translation = translations[normalizedType];
    if (translation) {
      return i18n._(translation);
    }
    return i18n._(translations.general_pattern, { 0: cleanType });
  }

  const submit = () => {
    if (props.customAction) {
      props.customAction(props.data.requestId)
      return
    }
    dispatch(
      initiateTransaction(
        {
          ...props.data.request,
          uid: uuid.v4(),
        },
        true
      ),
    )
  }

  const imsgSrc = React.useMemo(() => {
    const isSuccessful = props.data?.status?.toLocaleLowerCase() === 'successful';
    const isPending = props.data?.status?.toLocaleLowerCase() === 'pending';
    const isReversal = props.data.transactionType?.includes("REVERSAL")
    return isSuccessful && !isReversal ?
      greenArrow :
      isPending ?
        pendingIcon : redArrow
  }, [props.data])

  return (
    <div>
      <div style={itemStyle}>
        <img
          src={imsgSrc}
          alt=""
          style={{ alignSelf: "center", padding: "0 12px" }}
        />
        <div style={{ width: "100%" }}>
          <div style={textStyle}>
            <span>{props.data.transactionPartyName?.split("-")[0]}</span>{" "}
            <Status status={props.data.status} />
          </div>
          <span
            style={{
              display: "flex",
              justifyContent: "space-between",
              paddingTop: 4,
            }}
          >
            <span>
              {`${(props.data?.transactionType || props.data?.type) &&
                getCustomType(
                  props.data?.transactionType?.toLocaleLowerCase() || props.data?.type?.toLocaleLowerCase(),
                  props.data?.fund,
                )
                }:  ${i18n.locale === "ar"
                  ? intToArabic(props.data?.amount)
                  : props.data?.amount
                }`}
            </span>
            <span>
              {`${new Date(props.data?.createdAt).getHours()}:${new Date(
                props.data.createdAt,
              ).getMinutes()}:${new Date(props.data?.createdAt).getSeconds()}
              `}
            </span>
          </span>
        </div>
      </div>
      {props.data?.status && props.data?.status.toLocaleLowerCase() === "failed" ? (
        <>
          {props.data?.description}
          <button onClick={submit} style={buttonStyle}>
            {props.customBtnLabel || t`Retry`}
          </button>
        </>
      ) : (
        ""
      )}
    </div>
  )
}

View.propTypes = {
  customAction: PropTypes.func,
  customBtnLabel: PropTypes.string,
  data: PropTypes.shape({
    requestId: PropTypes.string,
    cacheId: PropTypes.string,
    request: PropTypes.object,
    status: PropTypes.string,
    transactionType: PropTypes.string,
    transactionPartyName: PropTypes.string,
    fund: PropTypes.string,
    amount: PropTypes.number,
    createdAt: PropTypes.string,
    description: PropTypes.string,
  }),
}

export default View
